import { useCallback, useState } from 'react';

export interface UseAuthGateOptions {
  /** Chave no localStorage que indica usuário logado (ex.: 'e-learning-b2b-auth') */
  authKey: string;
  /** Prefixo dos tokens de teste (ex.: 'jwt-teste-b2b') — chaves reais são access_token e refresh_token */
  tokenPrefix: string;
  /** Porta em modo standalone para detectar isStandalone */
  standalonePort: string;
  /** Basename do Router quando não standalone (ex.: '/b2b' ou '/') */
  basename: string;
  /** Chave do localStorage onde estão os dados completos de auth (ex.: 'e-learning-b2b-auth-data'). Se informada, será limpa no logout. */
  authDataKey?: string;
}

/** Chaves usadas pelo api client (core) — alinhadas com createLocalStorageTokenStorage */
const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';

export function useAuthGate({
  authKey,
  tokenPrefix,
  standalonePort,
  basename,
  authDataKey,
}: UseAuthGateOptions) {
  const readAuth = useCallback((): boolean => {
    try {
      return (
        typeof window !== 'undefined' && localStorage.getItem(authKey) === '1'
      );
    } catch {
      return false;
    }
  }, [authKey]);

  const [auth, setAuth] = useState(readAuth);

  const setTestTokensOnLogin = useCallback(() => {
    try {
      const testAccess = tokenPrefix + '-' + Date.now();
      const testRefresh = 'refresh-' + tokenPrefix + '-' + Date.now();
      localStorage.setItem(ACCESS_TOKEN_KEY, testAccess);
      localStorage.setItem(REFRESH_TOKEN_KEY, testRefresh);
    } catch {
      // ignore
    }
  }, [tokenPrefix]);

  const handleLogin = useCallback(() => {
    try {
      localStorage.setItem(authKey, '1');
      // Só grava tokens de teste se ainda não houver token real (login real já gravou access_token/refresh_token)
      if (!localStorage.getItem(ACCESS_TOKEN_KEY)) {
        setTestTokensOnLogin();
      }
      setAuth(true);
    } catch {
      // ignore
    }
  }, [authKey, setTestTokensOnLogin]);

  /** Limpa authKey, tokens (access_token, refresh_token), opcionalmente authDataKey e redireciona para a tela de login (basename). */
  const handleLogout = useCallback(() => {
    try {
      if (typeof window !== 'undefined') {
        localStorage.removeItem(authKey);
        localStorage.removeItem(ACCESS_TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        if (authDataKey) {
          localStorage.removeItem(authDataKey);
        }
        setAuth(false);
        const path = basename && basename !== '/' ? basename : '/';
        window.location.href = path;
      }
    } catch {
      // ignore
    }
  }, [authKey, authDataKey, basename]);

  const isStandalone =
    typeof window !== 'undefined' && window.location.port === standalonePort;

  return {
    auth,
    setAuth,
    readAuth,
    handleLogin,
    handleLogout,
    setTestTokensOnLogin,
    isStandalone,
    basename: isStandalone ? '' : basename,
  };
}
